import { useMutation, useQuery } from '@tanstack/react-query'
import { usePerson } from '../contexts/PersonContext'
import { OpenRoomsSection } from '../components/rooms/OpenRoomsSection'
import { Button } from '../components/ui/Button'
import { Avatar } from '../components/ui/Avatar'
import { SectionLabel } from '../components/ui/SectionLabel'
import { useToast } from '../contexts/ToastContext'
import { listFriends, pingCircle } from '../lib/api/friends'
import type { FriendDto } from '../types/community'

/** Kid "Play now" home (COTC_Rooms R4 / DESIGN §3.9). */
export function HomeKidPage() {
  const { flash } = useToast()
  const { activePerson, flags, guardianFirst, counters } = usePerson()
  const actingPersonId = activePerson?.id
  const kidFirst = flags.first || 'there'


  const onlineCount = counters.onlineFriends
  const greeting = onlineCount > 0 ? `Hey ${kidFirst} — ${onlineCount} ${onlineCount === 1 ? 'friend is' : 'friends are'} on.` : `Hey ${kidFirst}.`
  const subline = flags.viewOnly
    ? `${guardianFirst || 'A grown-up'} sends messages for you — you can watch and read along.`
    : flags.supervised
      ? `${guardianFirst || 'A grown-up'} sees everything you send here.`
      : 'Jump into a room, or ping your circle to get a game going.'

  // Online friends — best-effort; the chips section hides itself if the source isn't ready.
  const friendsQuery = useQuery<FriendDto[]>({
    queryKey: ['community', 'friends', 'online', actingPersonId ?? null],
    queryFn: () => listFriends(),
    enabled: !!actingPersonId,
    retry: false,
  })
  const onlineFriends = (friendsQuery.data ?? []).filter((f) => f.online)


  const ping = useMutation({
    mutationFn: () => pingCircle(),
    onSuccess: () => flash("Pinged your circle — they'll see you're around."),
    onError: () => flash('Could not ping just now — try again in a moment.'),
  })

  return (
    <div className="animate-rise px-5 py-6 sm:px-8 sm:py-8">
      <SectionLabel>Play now</SectionLabel>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">
        {greeting}
      </h1>
      <p className="mt-2 max-w-[56ch] text-[15px] leading-relaxed text-ink-muted">{subline}</p>

      <div className="mt-5">
        <Button
          variant="accent"
          onClick={() => actingPersonId && ping.mutate()}
          disabled={!actingPersonId || ping.isPending}
          className="shadow-[0_2px_0_#8d5031]"
        >
          Ping my circle
        </Button>
      </div>

      {onlineFriends.length > 0 && (
        <div className="mt-7">
          <SectionLabel>On right now</SectionLabel>
          <div className="mt-3 flex flex-wrap gap-2">
            {onlineFriends.map((f) => (
              <span
                key={f.personId}
                className="inline-flex items-center gap-2 rounded-pill border border-line-soft bg-paper-surface px-3 py-2"
              >
                <Avatar initials={(f.firstName || '?')[0].toUpperCase()} tintIndex={f.tintIndex} size={20} showDot online />
                <span className="text-[13px] font-medium text-ink">{f.firstName}</span>
                {f.metaText && <span className="font-mono text-[11px] text-ink-faint">{f.metaText}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <OpenRoomsSection />
    </div>
  )
}
